import PropTypes from "prop-types";
import {createContext, useCallback, useEffect, useReducer} from "react";
import {initializeApp} from "firebase/app";
import {getAuth, onAuthStateChanged, signInWithCustomToken, signInWithEmailAndPassword, signOut} from "firebase/auth";
import {getFirestore} from "firebase/firestore";
import {getDatabase, ref} from "firebase/database";
import {getStorage, ref as sRef} from "firebase/storage";

import {FIREBASE_API} from "../config";
import {clearSession, getIniLoggedUser, setIniLoggedUser} from "../helper/session";
import {resetTokenCache} from "../api/config";
import {changeUser, me} from "../api/auth";
import {resetIsFetchedUserSettings} from "../redux/slices/userSettings";
import {dispatch as appDispatch} from "../redux/store"

// ----------------------------------------------------------------------

const initialState = {
    isInitialized: false,
    isAuthenticated: false,
    user: null,
    isMainAccount: true,
};

const ACTION_TYPES = {
    INITIAL: "INITIAL",
};

const reducer = (state, action) => {
    if (action.type === ACTION_TYPES.INITIAL) {
        return {
            isInitialized: true,
            operator: undefined,
            isAuthenticated: action.payload.isAuthenticated,
            user: action.payload.user,
            isMainAccount: action.payload.isMainAccount
        };
    }

    return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

const firebaseApp = initializeApp(FIREBASE_API);

export const AUTH = getAuth(firebaseApp);

/**
 * Primarni nalog = korisnik kojim je urađena prijava, pre eventualnog prelaska
 * stanice na prevoznika (changeUser). Posle prelaska currentUser je prevoznik.
 */
export function isPrimaryUser() {
    return getIniLoggedUser()?.uid === AUTH.currentUser?.uid;
}
export const DB = getFirestore(firebaseApp);
export const RDB = getDatabase(firebaseApp);
export const dbRef = ref(RDB);
export const storageRef = getStorage(firebaseApp);
export const usersImageStorage = sRef(storageRef, "public/userphotos");
export const cityStorage = sRef(storageRef, "");

/**
 * Firebase posle osvežavanja stranice obnavlja prijavu asinhrono, pa je `AUTH.currentUser`
 * kratko null iako je korisnik prijavljen. Ekrani koji odmah čitaju `currentUser.uid` zato
 * povremeno pucaju — ovde se sačeka da se prijava obnovi (ili istekne vreme).
 */
export function waitForCurrentUser(timeoutMs = 10000) {
    if (AUTH.currentUser) {
        return Promise.resolve(AUTH.currentUser);
    }
    return new Promise((resolve) => {
        let settled = false;
        let unsubscribe;
        const finish = (user) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            if (unsubscribe) unsubscribe();
            resolve(user);
        };
        const timer = setTimeout(() => finish(AUTH.currentUser || null), timeoutMs);
        // Prvi poziv stiže odmah i sme biti null (prijava se tek obnavlja) — čeka se korisnik.
        unsubscribe = onAuthStateChanged(AUTH, (user) => {
            if (user) finish(user);
        });
    });
}

AuthProvider.propTypes = {
    children: PropTypes.node,
};

export function AuthProvider({children}) {
    const [state, dispatch] = useReducer(reducer, initialState);

    const initialize = useCallback(() => {
        try {
            onAuthStateChanged(AUTH, async (user) => {
                resetTokenCache();
                if (user) {
                    const userData = (await me()).data;

                    const initialUser = getIniLoggedUser();
                    const isMainAccount = initialUser ? initialUser.uid === user.uid : true;

                    dispatch({
                        type: ACTION_TYPES.INITIAL,
                        payload: {
                            isAuthenticated: true,
                            user: {...user, ...userData},
                            isMainAccount: isMainAccount
                        },
                    });
                } else {
                    dispatch({
                        type: ACTION_TYPES.INITIAL,
                        payload: {
                            isAuthenticated: false,
                            user: null,
                            isMainAccount: true
                        },
                    });
                }
            });
        } catch (error) {
            console.error(error);
        }
    }, []);

    useEffect(() => {
        initialize();
        return () => initialize();
        // eslint-disable-next-line
    }, []);

    // LOGIN
    const login = async (email, password) => {
        const userCredential = await signInWithEmailAndPassword(AUTH, email, password)
        const userData = (await me()).data;
        setIniLoggedUser({
            ...userCredential.user,
            ...userData
        })
    };

    // LOGOUT
    const logout = async () => {
        await signOut(AUTH);
        clearSession(true);
        dispatch({
            type: ACTION_TYPES.INITIAL,
            payload: {
                isAuthenticated: false,
                user: null,
                isMainAccount: true
            },
        });
    };

    const changeOperator = async (firebaseUid) => {
        const token = (await changeUser({
            userUid: firebaseUid
        })).data
        await signInWithCustomToken(AUTH, token)
        appDispatch(resetIsFetchedUserSettings());
    }

    return (
        <AuthContext.Provider
            value={{
                ...state,
                method: "firebase",
                login,
                logout,
                changeOperator
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}
